import { Activity, Database, Wifi, WifiOff, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { getAllOfflineEpisodes, getOfflineEpisodesTotalSize } from '@/lib/db'
import { formatBytes } from '@/lib/utils'

type NetworkEntry = {
  name: string
  transferSize: number
  decodedBodySize: number
  isCrossOrigin: boolean
  timestamp: number
}

/**
 * デバッグ用ネットワーク監視オーバーレイ
 *
 * - PerformanceObserver でリアルタイムのネットワーク転送量を表示
 * - バッファフル検知 + 自動クリアでエントリ取りこぼしを防止
 * - クロスオリジンで transferSize=0 のリクエストも表示
 */
export const DebugNetworkOverlay = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [transferredBytes, setTransferredBytes] = useState(0)
  const [requestCount, setRequestCount] = useState(0)
  const [skippedCount, setSkippedCount] = useState(0)
  const [bufferFullCount, setBufferFullCount] = useState(0)
  const [recentEntries, setRecentEntries] = useState<NetworkEntry[]>([])
  const [offlineSize, setOfflineSize] = useState(0)
  const [offlineCount, setOfflineCount] = useState(0)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const entriesRef = useRef<NetworkEntry[]>([])

  // ネットワーク状態監視
  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // バッファフル検知: バッファが満杯になったら自動クリア
  useEffect(() => {
    const handleBufferFull = () => {
      console.warn('[NetDebug] ⚠️ Resource timing buffer full! Clearing...')
      setBufferFullCount((prev) => prev + 1)
      performance.clearResourceTimings()
    }
    performance.addEventListener('resourcetimingbufferfull', handleBufferFull)
    // バッファサイズを大きくしておく
    performance.setResourceTimingBufferSize(500)

    return () => {
      performance.removeEventListener('resourcetimingbufferfull', handleBufferFull)
    }
  }, [])

  // PerformanceObserver でネットワーク転送を監視（常時稼働）
  useEffect(() => {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const r = entry as PerformanceResourceTiming
        const transferSize = r.transferSize ?? 0
        const decodedBodySize = r.decodedBodySize ?? 0
        const isCrossOrigin = transferSize === 0 && decodedBodySize === 0

        let pathname: string
        try {
          pathname = new URL(r.name).pathname
        } catch {
          pathname = r.name
        }

        // 全リクエストをログに出力（デバッグ用）
        console.log(
          `[NetDebug] ${isCrossOrigin ? '🔒' : '✅'} ${pathname}`,
          `transfer=${formatBytes(transferSize)}`,
          `decoded=${formatBytes(decodedBodySize)}`,
          `duration=${Math.round(r.duration)}ms`,
        )

        const newEntry: NetworkEntry = {
          name: pathname,
          transferSize,
          decodedBodySize,
          isCrossOrigin,
          timestamp: Date.now(),
        }

        entriesRef.current = [newEntry, ...entriesRef.current].slice(0, 30)
        setRecentEntries([...entriesRef.current])
        setRequestCount((prev) => prev + 1)

        if (isCrossOrigin) {
          setSkippedCount((prev) => prev + 1)
        } else {
          setTransferredBytes((prev) => prev + transferSize)
        }
      }
    })

    observer.observe({ type: 'resource', buffered: false })

    return () => observer.disconnect()
  }, [])

  // オフラインストレージ情報を取得
  useEffect(() => {
    if (!isOpen) return

    const load = async () => {
      const [size, episodes] = await Promise.all([getOfflineEpisodesTotalSize(), getAllOfflineEpisodes()])
      setOfflineSize(size)
      setOfflineCount(episodes.length)
    }
    load()

    const interval = setInterval(load, 10_000)
    return () => clearInterval(interval)
  }, [isOpen])

  const handleReset = useCallback(() => {
    setTransferredBytes(0)
    setRequestCount(0)
    setSkippedCount(0)
    setRecentEntries([])
    entriesRef.current = []
    performance.clearResourceTimings()
  }, [])

  // 閉じている状態: 小さなトグルボタン（転送量も表示）
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-3 z-10001 flex items-center gap-1 rounded-full bg-zinc-900/90 px-2.5 py-1.5 text-[10px] text-zinc-300 shadow-lg backdrop-blur-sm"
      >
        <Activity size={12} className={transferredBytes > 0 ? 'text-yellow-400' : 'text-green-400'} />
        {formatBytes(transferredBytes)} ({requestCount})
      </button>
    )
  }

  return (
    <div className="fixed bottom-20 right-3 z-10001 w-72 rounded-lg bg-zinc-900/95 text-xs text-zinc-200 shadow-2xl backdrop-blur-sm">
      {/* ヘッダー */}
      <div className="flex items-center justify-between border-b border-zinc-700 px-3 py-2">
        <div className="flex items-center gap-1.5 font-medium">
          <Activity size={14} className="text-green-400" />
          Network Debug
        </div>
        <button onClick={() => setIsOpen(false)} className="rounded p-0.5 hover:bg-zinc-700">
          <X size={14} />
        </button>
      </div>

      {/* ステータス */}
      <div className="grid grid-cols-2 gap-2 border-b border-zinc-700 px-3 py-2">
        {/* ネットワーク状態 */}
        <div className="flex items-center gap-1.5">
          {isOnline ? <Wifi size={12} className="text-green-400" /> : <WifiOff size={12} className="text-red-400" />}
          <span>{isOnline ? 'オンライン' : 'オフライン'}</span>
        </div>

        {/* オフラインストレージ */}
        <div className="flex items-center gap-1.5">
          <Database size={12} className="text-blue-400" />
          <span>
            {formatBytes(offlineSize)} ({offlineCount}件)
          </span>
        </div>
      </div>

      {/* 転送量 */}
      <div className="border-b border-zinc-700 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-zinc-400">セッション転送量</span>
          <div className="flex items-center gap-2">
            <span className={`font-mono font-bold ${transferredBytes > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
              {formatBytes(transferredBytes)}
            </span>
            <button onClick={handleReset} className="rounded bg-zinc-700 px-1.5 py-0.5 text-[10px] hover:bg-zinc-600">
              リセット
            </button>
          </div>
        </div>
        <div className="mt-1 flex gap-3 text-[10px] text-zinc-500">
          <span>リクエスト: {requestCount}</span>
          <span>🔒クロスオリジン: {skippedCount}</span>
          {bufferFullCount > 0 && <span className="text-red-400">⚠️バッファフル: {bufferFullCount}回</span>}
        </div>
        {transferredBytes === 0 && requestCount === 0 && (
          <p className="mt-1 text-[10px] text-green-400/80">ネットワーク転送なし（ローカル再生中）</p>
        )}
      </div>

      {/* 最近のリクエスト */}
      <div className="max-h-48 overflow-y-auto px-3 py-2">
        <p className="mb-1 text-zinc-400">最近のリクエスト</p>
        {recentEntries.length === 0 ? (
          <p className="text-[10px] text-zinc-500">リクエストなし</p>
        ) : (
          <div className="space-y-0.5">
            {recentEntries.map((entry, i) => (
              <div key={`${entry.timestamp}-${i}`} className="flex items-center justify-between gap-2">
                <span className="truncate text-[10px] text-zinc-400" title={entry.name}>
                  {entry.isCrossOrigin ? '🔒 ' : ''}
                  {entry.name}
                </span>
                <span
                  className={`shrink-0 font-mono text-[10px] ${entry.isCrossOrigin ? 'text-zinc-500' : 'text-yellow-400'}`}
                >
                  {entry.isCrossOrigin ? 'cross-origin' : formatBytes(entry.transferSize)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
